import api from ".";
import { getToken } from "../Auth/Token";

const ORDER_COLLECTION_KEYS = ["orders", "data", "results", "items", "docs"];
const DEFAULT_ORDER_PAGE_SIZE = 500;

const getPaginationValue = (payload, keys) => {
  for (const key of keys) {
    if (payload?.[key] != null) {
      return payload[key];
    }

    if (payload?.pagination?.[key] != null) {
      return payload.pagination[key];
    }
  }

  return null;
};

const normalizeOrder = (order, index) => {
  const source = order?.amazonOrderDetails || order?.details || order || {};
  const salesOrderNumber =
    source?.salesOrderNumber ||
    source?.SalesOrderNumber ||
    source?.existingSalesOrderNumber ||
    order?.salesOrderNumber ||
    order?.existingSalesOrderNumber ||
    null;
  const salesOrderId =
    source?.salesOrderId ||
    source?.SalesOrderId ||
    source?.existingSalesOrderId ||
    order?.salesOrderId ||
    order?.existingSalesOrderId ||
    null;

  return {
    id:
      order?._id ||
      source?._id ||
      source?.AmazonOrderId ||
      source?.amazonOrderId ||
      `amazon-order-${index}`,
    amazonOrderId:
      source?.AmazonOrderId ||
      source?.amazonOrderId ||
      source?.orderId ||
      "N/A",
    marketplaceId:
      source?.MarketplaceId || source?.marketplaceId || "N/A",
    orderStatus: source?.OrderStatus || source?.orderStatus || "Unknown",
    automationStatus:
      source?.automationStatus || order?.automationStatus || "N/A",
    queuedAt: source?.queuedAt || order?.queuedAt || null,
    processingStartedAt:
      source?.processingStartedAt || order?.processingStartedAt || null,
    processedAt: source?.processedAt || order?.processedAt || null,
    queueJobId: source?.queueJobId || order?.queueJobId || "N/A",
    purchaseDate:
      source?.PurchaseDate || source?.purchaseDate || source?.createdAt || null,
    lastUpdateDate:
      source?.LastUpdateDate ||
      source?.lastUpdateDate ||
      source?.updatedAt ||
      null,
    fulfillmentChannel:
      source?.FulfillmentChannel || source?.fulfillmentChannel || "N/A",
    salesChannel: source?.SalesChannel || source?.salesChannel || "N/A",
    orderTotal:
      source?.OrderTotal?.Amount ||
      source?.orderTotal?.amount ||
      source?.orderTotalAmount ||
      source?.OrderAmount ||
      null,
    currencyCode:
      source?.OrderTotal?.CurrencyCode ||
      source?.orderTotal?.currencyCode ||
      source?.orderTotalCurrencyCode ||
      source?.CurrencyCode ||
      null,
    salesOrderNumber: salesOrderNumber || "N/A",
    salesOrderId: salesOrderId || "N/A",
    useExistingSalesOrder:
      source?.useExistingSalesOrder ??
      source?.shouldUseExistingSalesOrder ??
      order?.useExistingSalesOrder ??
      order?.shouldUseExistingSalesOrder ??
      null,
    automationAction:
      source?.automationAction ||
      source?.automationRoute ||
      source?.salesOrderAction ||
      order?.automationAction ||
      order?.automationRoute ||
      order?.salesOrderAction ||
      null,
    sellerSkus:
      source?.sellerSkus ||
      source?.SellerSKU ||
      source?.sellerSku ||
      order?.sellerSkus ||
      "N/A",
    buyerName: source?.BuyerInfo?.BuyerName || source?.buyerName || "N/A",
    shipServiceLevel:
      source?.ShipServiceLevel || source?.shipServiceLevel || "N/A",
    orderItems: source?.OrderItems || source?.orderItems || [],
    raw: order,
  };
};

const extractOrders = (payload) => {
  if (Array.isArray(payload)) return payload;

  for (const key of ORDER_COLLECTION_KEYS) {
    if (Array.isArray(payload?.[key])) {
      return payload[key];
    }
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const getPaginationState = (payload, orderCount) => {
  const totalPages = Number(
    getPaginationValue(payload, ["totalPages", "pages", "pageCount", "lastPage"]),
  );
  const currentPage = Number(
    getPaginationValue(payload, ["page", "currentPage", "pageNumber"]),
  );
  const nextPage = Number(getPaginationValue(payload, ["nextPage"]));
  const totalCount = Number(
    getPaginationValue(payload, [
      "total",
      "totalCount",
      "count",
      "totalDocs",
      "totalResults",
    ]),
  );
  const hasNextPageValue = getPaginationValue(payload, ["hasNextPage"]);

  return {
    totalPages: Number.isFinite(totalPages) ? totalPages : null,
    currentPage: Number.isFinite(currentPage) ? currentPage : null,
    nextPage: Number.isFinite(nextPage) ? nextPage : null,
    totalCount: Number.isFinite(totalCount) ? totalCount : null,
    hasNextPage:
      typeof hasNextPageValue === "boolean"
        ? hasNextPageValue
        : Number.isFinite(nextPage)
          ? nextPage > 0
          : Number.isFinite(totalPages) && Number.isFinite(currentPage)
            ? currentPage < totalPages
            : Number.isFinite(totalCount)
              ? orderCount < totalCount
              : false,
  };
};

const fetchStoredAmazonOrdersPage = async (token, page, limit) => {
  return api.get("/api/erpag/amazon/orders/stored", {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    params: {
      page,
      limit,
    },
  });
};

export const getStoredAmazonOrders = async () => {
  const token = getToken();
  const firstResponse = await fetchStoredAmazonOrdersPage(
    token,
    1,
    DEFAULT_ORDER_PAGE_SIZE,
  );
  const firstPayload = firstResponse.data;
  const firstBatch = extractOrders(firstPayload);
  const paginationState = getPaginationState(firstPayload, firstBatch.length);

  let combinedOrders = [...firstBatch];

  if (paginationState.hasNextPage) {
    const seenIds = new Set(
      firstBatch.map((order, index) => normalizeOrder(order, index).id),
    );
    let nextPage = paginationState.nextPage || 2;
    const finalPage = paginationState.totalPages;

    while (
      nextPage &&
      (!finalPage || nextPage <= finalPage)
    ) {
      const response = await fetchStoredAmazonOrdersPage(
        token,
        nextPage,
        DEFAULT_ORDER_PAGE_SIZE,
      );
      const payload = response.data;
      const batch = extractOrders(payload);

      if (!batch.length) {
        break;
      }

      batch.forEach((order, index) => {
        const normalized = normalizeOrder(order, combinedOrders.length + index);
        if (!seenIds.has(normalized.id)) {
          seenIds.add(normalized.id);
          combinedOrders.push(order);
        }
      });

      const state = getPaginationState(payload, combinedOrders.length);
      if (!state.hasNextPage) {
        break;
      }

      nextPage = state.nextPage || nextPage + 1;
    }
  }

  const orders = combinedOrders.map(normalizeOrder);

  return {
    ...firstPayload,
    orders,
  };
};

export const addWarehouseAndFulfillAmazonOrder = async (payload) => {
  const token = getToken();
  const requestPayload = {
    ...payload,
    priority: payload?.priority || "highest",
    requestedFrom: payload?.requestedFrom || "ui",
  };
  const response = await api.post(
    "/api/erpag/salesorder/addwarehouse-and-fulfill",
    requestPayload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  return response.data;
};
